/**
 * Copyright 2018 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import T from "i18n-react/dist/i18n-react";
import debounce from "lodash/debounce";
import {
  getRequest,
  putRequest,
  postRequest,
  deleteRequest,
  createAction,
  stopLoading,
  startLoading,
  fetchResponseHandler,
  fetchErrorHandler,
  escapeFilterValue,
  snackbarErrorHandler,
  snackbarSuccessHandler
} from "openstack-uicore-foundation/lib/utils/actions";
import URI from "urijs";
import { getAccessTokenSafely } from "../utils/methods";
import {
  DEBOUNCE_WAIT,
  DEFAULT_PER_PAGE,
  FIVE_PER_PAGE
} from "../utils/constants";

export const REQUEST_MEDIA_FILE_TYPES = "REQUEST_MEDIA_FILE_TYPES";
export const RECEIVE_MEDIA_FILE_TYPES = "RECEIVE_MEDIA_FILE_TYPES";
export const REQUEST_ALL_MEDIA_FILE_TYPES = "REQUEST_ALL_MEDIA_FILE_TYPES";
export const RECEIVE_ALL_MEDIA_FILE_TYPES = "RECEIVE_ALL_MEDIA_FILE_TYPES";
export const RECEIVE_MEDIA_FILE_TYPE = "RECEIVE_MEDIA_FILE_TYPE";
export const RESET_MEDIA_FILE_TYPE_FORM = "RESET_MEDIA_FILE_TYPE_FORM";
export const UPDATE_MEDIA_FILE_TYPE = "UPDATE_MEDIA_FILE_TYPE";
export const MEDIA_FILE_TYPE_UPDATED = "MEDIA_FILE_TYPE_UPDATED";
export const MEDIA_FILE_TYPE_ADDED = "MEDIA_FILE_TYPE_ADDED";
export const MEDIA_FILE_TYPE_DELETED = "MEDIA_FILE_TYPE_DELETED";

export const getMediaFileTypes =
  (
    term = null,
    page = 1,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = 1
  ) =>
  async (dispatch) => {
    const accessToken = await getAccessTokenSafely();
    const filter = [];

    dispatch(startLoading());

    const params = {
      page,
      per_page: perPage,
      access_token: accessToken
    };

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm}`);
    }

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_MEDIA_FILE_TYPES),
      createAction(RECEIVE_MEDIA_FILE_TYPES),
      `${window.API_BASE_URL}/api/v1/summit-media-file-types`,
      snackbarErrorHandler,
      { order, orderDir, term, perPage }
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

export const getAllMediaFileTypes = () => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    page: 1,
    per_page: 100
  };

  return getRequest(
    createAction(REQUEST_ALL_MEDIA_FILE_TYPES),
    createAction(RECEIVE_ALL_MEDIA_FILE_TYPES),
    `${window.API_BASE_URL}/api/v1/summit-media-file-types`,
    snackbarErrorHandler,
    {}
  )(params)(dispatch).finally(() => {
    dispatch(stopLoading());
  });
};

export const getMediaFileType = (mediaFileTypeId) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  return getRequest(
    null,
    createAction(RECEIVE_MEDIA_FILE_TYPE),
    `${window.API_BASE_URL}/api/v1/summit-media-file-types/${mediaFileTypeId}`,
    snackbarErrorHandler
  )(params)(dispatch).finally(() => {
    dispatch(stopLoading());
  });
};

export const resetMediaFileTypeForm = () => (dispatch) => {
  dispatch(createAction(RESET_MEDIA_FILE_TYPE_FORM)({}));
};

export const saveMediaFileType = (entity) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const normalizedEntity = normalizeEntity(entity);
  const params = { access_token: accessToken };

  if (entity.id) {
    return putRequest(
      createAction(UPDATE_MEDIA_FILE_TYPE),
      createAction(MEDIA_FILE_TYPE_UPDATED),
      `${window.API_BASE_URL}/api/v1/summit-media-file-types/${entity.id}`,
      normalizedEntity,
      snackbarErrorHandler,
      entity
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("media_file_type.saved")
          })
        );
      })
      .finally(() => dispatch(stopLoading()));
  }

  return postRequest(
    createAction(UPDATE_MEDIA_FILE_TYPE),
    createAction(MEDIA_FILE_TYPE_ADDED),
    `${window.API_BASE_URL}/api/v1/summit-media-file-types`,
    normalizedEntity,
    snackbarErrorHandler,
    entity
  )(params)(dispatch)
    .then(() => {
      dispatch(
        snackbarSuccessHandler({
          title: T.translate("general.success"),
          html: T.translate("media_file_type.created")
        })
      );
    })
    .finally(() => dispatch(stopLoading()));
};

export const deleteMediaFileType = (mediaFileTypeId) => async (dispatch) => {
  dispatch(startLoading());
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return deleteRequest(
    null,
    createAction(MEDIA_FILE_TYPE_DELETED)({ mediaFileTypeId }),
    `${window.API_BASE_URL}/api/v1/summit-media-file-types/${mediaFileTypeId}`,
    null,
    snackbarErrorHandler
  )(params)(dispatch).finally(() => {
    dispatch(stopLoading());
  });
};

const normalizeEntity = (entity) => {
  const normalizedEntity = { ...entity };

  delete normalizedEntity.id;
  delete normalizedEntity.created;
  delete normalizedEntity.modified;

  normalizedEntity.allowed_extensions = entity.allowed_extensions.split(",");

  return normalizedEntity;
};

export const queryMediaFileTypes = debounce(async (input, callback) => {
  const accessToken = await getAccessTokenSafely();
  const apiUrl = URI(`${window.API_BASE_URL}/api/v1/summit-media-file-types`);

  apiUrl.addQuery("access_token", accessToken);
  apiUrl.addQuery("order", "name");
  apiUrl.addQuery("per_page", FIVE_PER_PAGE);

  if (input) {
    input = escapeFilterValue(input);
    apiUrl.addQuery("filter[]", `name=@${input}`);
  }

  fetch(apiUrl.toString())
    .then(fetchResponseHandler)
    .then((json) => {
      const options = [...json.data];
      callback(options);
    })
    .catch(fetchErrorHandler);
}, DEBOUNCE_WAIT);
